/**
 * Organization data models.
 */

import { BaseEntity, TimestampMixin, ActiveEntity } from './base';

export interface Plan {
  id?: string;
  /** Present on new plan shape; legacy orgs may only have id or limits */
  name?: string; // e.g., Free, Pro, Enterprise
  monthlyPrice?: number;
  currency?: string;
  billingType?: 'fixed' | 'quote';
  maxSalesPeople?: number; // maximum number of sales people allowed
  maxCustomers?: number; // maximum number of customers allowed
  maxInventoryItems?: number; // maximum number of inventory items allowed
  maxSuppliers?: number; // maximum number of suppliers allowed
  maxAdmins?: number; // maximum number of admin users allowed
}

export interface BillingInfo {
  // Stripe core identifiers
  stripeClientId?: string; // OAuth client id if using Connect
  stripeCustomerId?: string; // cus_*
  stripeSubscriptionId?: string; // sub_*
  stripePriceId?: string; // price_*
  defaultPaymentMethodId?: string; // pm_*

  // Subscription status/period
  subscriptionStatus?:
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'unpaid'
    | 'incomplete'
    | 'incomplete_expired'
    | 'paused'
    | 'pending_ves'   // VES payment submitted, awaiting admin approval
    | 'ves_rejected'; // VES payment rejected by admin – blocks app access
  currentPeriodEnd?: Date;

  // Additional metadata
  metadata?: Record<string, string>;

  /** ID of VES payment request when subscriptionStatus is pending_ves or ves_rejected */
  vesPaymentRequestId?: string;

  /** True if org has already used the 30-day free trial (no second trial) */
  hasUsedFreeTrial?: boolean;

  /** Payment method: 'ves' = Bolívares (prompt within first 10 days of month, restrict after) */
  paymentMethod?: 'ves' | 'stripe' | 'binance';
  /** Allowed subscription payment methods */
  paymentMethodsAllowed?: Array<'ves' | 'stripe' | 'binance'>;

  /** Last paid/assigned plan id */
  planId?: string;

  /**
   * When set, only these plan IDs are shown in the Paywall plan selector (e.g. when trialing or when org can choose).
   * When empty/undefined, all fixed-price plans from the catalog are shown.
   */
  selectablePlanIds?: string[];

  /** Intelligence capacity entitlement (Lite / Plus / Extra) */
  intelligence?: {
    capacityId?: 'lite' | 'plus' | 'extra' | null;
    creditsAllowance?: number;
    creditsUsed?: number;
    periodEnd?: Date;
    price?: number;
    currency?: string;
    stripeSubscriptionItemId?: string | null;
  };
}

/** Tipo de contribuyente (Venezuela): determina si aplica retención de IVA */
export type TaxpayerType = 'ordinario' | 'especial' | 'formal' | 'exento';

/** Fiscal data for a single jurisdiction (e.g. Venezuela VES). Other jurisdictions can add their own shape later. */
export interface VesFiscalData {
  rif?: string;
  taxpayerType?: TaxpayerType;
  withholdingAgent?: boolean;
  fiscalAddress?: string;
  fiscalState?: string;
  fiscalMunicipality?: string;
}

/** Fiscal data keyed by jurisdiction code (VES, US, etc.) so the org model does not bloat per country. */
export type FiscalDataByJurisdiction = {
  /** Venezuela fiscal data (VES). Other jurisdictions may have different shapes. */
  VES?: VesFiscalData;
} & Record<string, unknown>;

export type OnboardingStatus = 'active' | 'dismissed' | 'completed';
export type OnboardingStepId =
  | 'intro'
  | 'product'
  | 'client'
  | 'order'
  | 'approve'
  | 'done';

/** Guided first-sale tour on the organization. Absent ⇒ treat as completed (legacy). */
export interface OrganizationOnboarding {
  version: number;
  status: OnboardingStatus;
  currentStepId: OnboardingStepId;
  /** When true, walk from intro ignoring live counts for current step (re-activate). */
  replay?: boolean;
  startedAt?: string | Date | null;
  dismissedAt?: string | Date | null;
  celebratedAt?: string | Date | null;
  updatedAt?: string | Date | null;
  updatedByUserId?: string | null;
}

export interface OrganizationOnboardingStepView {
  id: OnboardingStepId;
  done: boolean;
}

export interface OrganizationOnboardingResponse {
  onboarding: OrganizationOnboarding | null;
  steps: OrganizationOnboardingStepView[];
  derivedCurrentStepId: OnboardingStepId | null;
  isComplete: boolean;
  showTour: boolean;
  counts: {
    products: number;
    clients: number;
    orders: number;
    approvedOrders: number;
  };
}

export interface OrganizationOnboardingUpdate {
  status?: OnboardingStatus;
  currentStepId?: OnboardingStepId;
  replay?: boolean;
  dismissedAt?: string | Date | null;
  celebratedAt?: string | Date | null;
}

export interface OrganizationBase extends ActiveEntity {
  name: string;
  description?: string;
  industry?: string;
  website?: string;
  address?: string;
  phone?: string;
  email?: string;
  taxId?: string; // RIF or equivalent
  logoUrl?: string;
  customDomain?: string; // Custom domain for the organization
  customDomainStatus?: 'pending' | 'active' | 'failed'; // Status of custom domain
  isProfileComplete?: boolean;
  settings?: OrganizationSettings;
  plan?: Plan;
  billing?: BillingInfo;
  /** Fiscal data by jurisdiction (VES, US, …). Keeps model unscoped per country. */
  fiscalDataByJurisdiction?: FiscalDataByJurisdiction;
  /**
   * Guided first-sale onboarding. Missing/undefined ⇒ completed (backward compat).
   * Only seeded for orgs created after this feature shipped.
   */
  onboarding?: OrganizationOnboarding | null;
  /** True when this org is the isolated test/sandbox twin of a production org */
  isSandbox?: boolean;
  /** Set on sandbox orgs — Firestore id of the production organization */
  sandboxParentOrganizationId?: string;
  /** Set on production orgs — Firestore id of the linked sandbox organization (at most one) */
  sandboxOrganizationId?: string;
}

/**
 * Returns VES fiscal data from the organization (nested or legacy flat fields for backward compat).
 */
export function getVesFiscalData(organization: OrganizationBase | null): VesFiscalData | null {
  if (!organization) return null;
  const nested = organization.fiscalDataByJurisdiction?.['VES'];
  if (nested && (nested.rif ?? nested.fiscalAddress ?? nested.fiscalState ?? nested.fiscalMunicipality)) {
    return nested;
  }
  // Legacy: flat fields (deprecated) – Firestore may still have these at root
  const o = organization as unknown as Record<string, unknown>;
  const rif = o.rif ?? organization.taxId;
  const taxpayerType = o.taxpayerType;
  const withholdingAgent = o.withholdingAgent;
  const fiscalAddress = o.fiscalAddress;
  const fiscalState = o.fiscalState;
  const fiscalMunicipality = o.fiscalMunicipality;
  if (!rif && !fiscalAddress && !fiscalState && !fiscalMunicipality) return null;
  return {
    rif: typeof rif === 'string' ? rif : undefined,
    taxpayerType: taxpayerType as VesFiscalData['taxpayerType'],
    withholdingAgent: typeof withholdingAgent === 'boolean' ? withholdingAgent : undefined,
    fiscalAddress: typeof fiscalAddress === 'string' ? fiscalAddress : undefined,
    fiscalState: typeof fiscalState === 'string' ? fiscalState : undefined,
    fiscalMunicipality: typeof fiscalMunicipality === 'string' ? fiscalMunicipality : undefined,
  };
}

/** Returns true if the org has existing VES fiscal data (nested or legacy). */
export function hasVesFiscalData(organization: OrganizationBase | null): boolean {
  const ves = getVesFiscalData(organization);
  if (!ves) return false;
  return !!(ves.rif?.trim() || ves.fiscalAddress?.trim() || ves.fiscalState?.trim() || ves.fiscalMunicipality?.trim());
}

export interface OrganizationSettings {
  timezone: string;
  language: string;
  dateFormat: string;
  fiscalYearStart: string; // MM-DD format
  defaultSalesZoneId?: string;
  // Note: enableNotifications, enableMultiCurrency, and inventoryManagement
  // are now handled by factors collection - see ConfigurationFactors interface
  // Note: currency, useReferenceCurrency, referenceCurrency, and exchangeRate
  // are now handled by factors collection - see ConfigurationFactors interface
}

export interface Organization
  extends OrganizationBase,
    BaseEntity,
    TimestampMixin {
  // Inherits all fields from OrganizationBase + BaseEntity + TimestampMixin
}

export interface OrganizationCreate
  extends Omit<OrganizationBase, 'settings' | 'plan' | 'billing'> {
  settings?: Partial<OrganizationSettings>;
  plan?: Partial<Plan>;
  billing?: Partial<BillingInfo>;
}

export interface OrganizationUpdate extends Partial<OrganizationBase> {
  updated?: Date;
}

// Supported currencies with Venezuelan Bolivar as default
export const SUPPORTED_CURRENCIES = [
  {
    code: 'VES',
    name: 'Bolívar Venezolano',
    symbol: 'Bs',
    country: 'Venezuela',
  },
  {
    code: 'USD',
    name: 'Dólar Estadounidense',
    symbol: '$',
    country: 'Estados Unidos',
  },
  { code: 'EUR', name: 'Euro', symbol: '€', country: 'Unión Europea' },
  { code: 'COP', name: 'Peso Colombiano', symbol: '$', country: 'Colombia' },
  { code: 'ARS', name: 'Peso Argentino', symbol: '$', country: 'Argentina' },
  { code: 'BRL', name: 'Real Brasileño', symbol: 'R$', country: 'Brasil' },
  { code: 'MXN', name: 'Peso Mexicano', symbol: '$', country: 'México' },
  { code: 'CLP', name: 'Peso Chileno', symbol: '$', country: 'Chile' },
] as const;

// Default values for organizations
export const ORGANIZATION_DEFAULTS = {
  active: true,
  description: '',
  industry: '',
  timezone: 'America/Caracas',
  language: 'es',
  dateFormat: 'DD/MM/YYYY',
  fiscalYearStart: '01-01',
  // Note: enableNotifications, enableMultiCurrency, and inventoryManagement
  // are now handled by factors collection
  // Note: currency, useReferenceCurrency, referenceCurrency, and exchangeRate
  // are now handled by factors collection
} as const;

// Default organization settings
export const DEFAULT_ORGANIZATION_SETTINGS: OrganizationSettings = {
  timezone: ORGANIZATION_DEFAULTS.timezone,
  language: ORGANIZATION_DEFAULTS.language,
  dateFormat: ORGANIZATION_DEFAULTS.dateFormat,
  fiscalYearStart: ORGANIZATION_DEFAULTS.fiscalYearStart,
  // Note: enableNotifications, enableMultiCurrency, and inventoryManagement
  // are now handled by factors collection
  // Note: currency, useReferenceCurrency, referenceCurrency, and exchangeRate
  // are now handled by factors collection
};

// Default plan limits
export const DEFAULT_PLAN: Plan = {
  name: 'Unassigned',
  maxSalesPeople: 0,
  maxCustomers: 0,
  maxInventoryItems: 0,
  maxAdmins: 0,
};

// Note: getAllowAdminCurrencyModification helper function removed since
// allowAdminCurrencyModification is now handled by factors collection

// Organization search and filter types
export interface OrganizationFilters {
  searchTerm?: string;
  industry?: string;
  active?: boolean;
}

export interface OrganizationQueryResult {
  organizations: Organization[];
  lastDoc?: any; // Firestore document snapshot
  hasMore: boolean;
  totalCount?: number;
}

// Organization context for React
export interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
}
